import React, { useState, useEffect } from 'react';
import { Global } from '@emotion/react';
import TextField from '@mui/material/TextField';
import { StandaloneSearchBox } from '@react-google-maps/api';
import { useGoogleTranslator } from '@/hooks/useGoogleTranslator';
import { Location } from '@/graphql/types';
import styles, { pacContainerGlobalStyles } from './Autocomplete.styles';

const COMBOBOX_POPUP_ID = 'center-of-map-suggestions';

interface Props {
  updateMapPosition: (location: Location) => void;
  textSearch: string;
  setTextSearch: (TextSearch: string) => void;
  setLocalArea: (localArea: string) => void;
  hasError: boolean;
}

export const Autocomplete = ({
  updateMapPosition,
  textSearch,
  setTextSearch,
  setLocalArea,
  hasError,
}: Props) => {
  const [searchBox, setSearchBox] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [popupId, setPopupId] = useState<string | null>(null);

  const searchInstructionTranslated = useGoogleTranslator(
    'Search by City, Street Address, or Zip code'
  );
  const labelTranslated = useGoogleTranslator('Center of Map');
  const messageErrorTranslated = useGoogleTranslator('Center of Map is required');
  const listboxLabelTranslated = useGoogleTranslator('Location suggestions');

  useEffect(() => {
    const attachIdToPopup = (container: Element) => {
      if (!container.id) {
        // eslint-disable-next-line no-param-reassign -- setting the id on the DOM element is the point of this function
        container.id = COMBOBOX_POPUP_ID;
        container.setAttribute('role', 'listbox');
        container.setAttribute('aria-label', listboxLabelTranslated);
        setPopupId(COMBOBOX_POPUP_ID);
      }
    };

    const observer = new MutationObserver(() => {
      const pacContainer = document.querySelector('.pac-container');
      if (pacContainer) {
        attachIdToPopup(pacContainer);
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    const pacContainer = document.querySelector('.pac-container');
    if (pacContainer) {
      attachIdToPopup(pacContainer);
    }

    return () => observer.disconnect();
  }, [listboxLabelTranslated]);

  const handlePlaceChanged = () => {
    // @ts-ignore
    const [place] = searchBox?.getPlaces();

    if (place) {
      const city = place.address_components.find(address =>
        address.types.includes('administrative_area_level_2')
      );
      if (city) {
        setLocalArea(city.long_name);
      }

      setTextSearch(place.name);
      updateMapPosition({
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      });
    }
  };
  const onSBLoad = ref => {
    setSearchBox(ref);
  };

  const onSearchChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) =>
    setTextSearch(event.target.value);

  const handleFocus = () => setIsExpanded(true);
  const handleBlur = () => setIsExpanded(false);

  return (
    <>
      <Global styles={pacContainerGlobalStyles} />
      <StandaloneSearchBox
        // eslint-disable-next-line no-return-assign
        onLoad={onSBLoad}
        onPlacesChanged={handlePlaceChanged}
      >
        <TextField
          id="center-of-map-input"
          label={labelTranslated}
          placeholder=""
          fullWidth
          variant="standard"
          size="small"
          value={textSearch || ''}
          onChange={onSearchChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className={styles.textfield}
          error={hasError}
          helperText={
            <>
              {hasError && (
                <>
                  {' '}
                  <span>{messageErrorTranslated}</span> <br />
                </>
              )}
              <span className={styles.searchFieldDescription}>{searchInstructionTranslated}</span>
            </>
          }
          data-testid="autocompleteInput"
          inputProps={{
            role: 'combobox',
            'aria-autocomplete': 'list',
            'aria-haspopup': 'listbox',
            'aria-expanded': isExpanded,
            'aria-labelledby': 'center-of-map-input-label',
            ...(popupId && { 'aria-controls': popupId }),
          }}
        />
      </StandaloneSearchBox>
    </>
  );
};
